export interface DashboardTile {
  name: string;
  icon: string;
  iconClass?: string;
  route: string;
  permission?: string;
  /** Optional subsection label (must match a `DashboardGroup.label` on the parent category). */
  group?: string;
}

export interface DashboardGroup {
  label: string;
  icon: string;
  iconClass?: string;
}

export interface DashboardCategory {
  key: string;
  title: string;
  icon: string;
  zone: 'workspace' | 'admin';
  items: DashboardTile[];
  /** Optional ordered subsections used to split a category's tiles (e.g. Music vs Video). */
  groups?: DashboardGroup[];
}

export const DashboardCategories: DashboardCategory[] = [
  {
    key: 'content',
    title: 'Content',
    icon: 'perm_media',
    zone: 'workspace',
    items: [
      {
        name: 'Audios',
        icon: 'headset',
        route: '/audios',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Videos',
        icon: 'movie',
        route: '/videos',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Apps',
        icon: 'apps',
        route: '/apps',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Images',
        icon: 'photo_library',
        route: '/images',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Articles',
        icon: 'news',
        iconClass: 'material-symbols-outlined',
        route: '/articles',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Books',
        icon: 'menu_book',
        iconClass: 'material-symbols-outlined',
        route: '/books',
        permission: 'GALAXY_READER',
      },
    ],
  },
  {
    key: 'gamification',
    title: 'Gamification',
    icon: 'sports_esports',
    zone: 'workspace',
    items: [
      {
        name: 'Instant games',
        icon: 'stadia_controller',
        iconClass: 'material-symbols-outlined',
        route: '/instant-games',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Quizzes',
        icon: 'quiz',
        iconClass: 'material-symbols-outlined',
        route: '/quizzes',
        permission: 'GALAXY_READER',
      },
    ],
  },
  {
    key: 'catalog',
    title: 'Catalog & Carts',
    icon: 'inventory_2',
    zone: 'workspace',
    items: [
      {
        name: 'Contents',
        icon: 'folder',
        route: '/contents',
        permission: 'GALAXY_CONTENTS',
      },
      {
        name: 'Carts',
        icon: 'shopping_cart',
        route: '/carts',
        permission: 'GALAXY_CONTENTS',
      },
    ],
  },
  {
    key: 'collections',
    title: 'Collections',
    icon: 'video_library',
    zone: 'workspace',
    items: [
      {
        name: 'Series',
        icon: 'live_tv',
        route: '/series',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Live TV',
        icon: 'tv',
        route: '/channels',
        permission: 'GALAXY_ADMIN_LIVE_TV',
      },
      {
        name: 'Music',
        icon: 'library_music',
        iconClass: 'material-symbols-outlined',
        route: '/music',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Publications',
        icon: 'library_books',
        iconClass: 'material-symbols-outlined',
        route: '/publications',
        permission: 'GALAXY_READER',
      },
    ],
  },
  {
    key: 'media-imports',
    title: 'Media Imports',
    icon: 'play_circle',
    zone: 'workspace',
    groups: [
      { label: 'Music', icon: 'headset' },
      { label: 'Video', icon: 'movie' },
    ],
    items: [
      {
        name: 'Import Music',
        icon: 'save_alt',
        route: '/import-audio-products',
        permission: 'GALAXY_ADMIN_IMPORT_AUDIO',
        group: 'Music',
      },
      {
        name: 'Media Explorer',
        icon: 'folder_match',
        iconClass: 'material-symbols-outlined',
        route: '/import-videos',
        permission: 'GALAXY_ADMIN_IMPORT_VIDEO',
        group: 'Video',
      },
      {
        name: 'Video Encoding',
        icon: 'code',
        iconClass: 'material-symbols-outlined',
        route: '/encode-videos',
        permission: 'GALAXY_ADMIN_IMPORT_VIDEO',
        group: 'Video',
      },
      {
        name: 'Encoding Queues',
        icon: 'hourglass_bottom',
        iconClass: 'material-symbols-outlined',
        route: '/queue-videos',
        permission: 'GALAXY_ADMIN_IMPORT_VIDEO',
        group: 'Video',
      },
      {
        name: 'Update Asset',
        icon: 'perm_media',
        iconClass: 'material-symbols-outlined',
        route: '/process-list/asset',
        permission: 'GALAXY_READER',
        group: 'Video',
      },
    ],
  },
  {
    key: 'media-processes',
    title: 'Media Processes',
    icon: 'pending_actions',
    zone: 'workspace',
    items: [
      {
        name: 'AI translation',
        icon: 'translate',
        iconClass: 'material-symbols-outlined',
        route: '/process-list/ai-translation',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Subtitle validation',
        icon: 'fact_check',
        iconClass: 'material-symbols-outlined',
        route: '/process-list/subtitle-validation',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Transcode',
        icon: 'movie_edit',
        iconClass: 'material-symbols-outlined',
        route: '/process-list/transcode',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Article from Video',
        icon: 'article',
        iconClass: 'material-symbols-outlined',
        route: '/process-list/article-from-video',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Speech-to-text',
        icon: 'subtitles',
        iconClass: 'material-symbols-outlined',
        route: '/process-list/speech-to-text',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Extract audio',
        icon: 'audio_file',
        iconClass: 'material-symbols-outlined',
        route: '/process-list/extract-audio',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Restoration',
        icon: 'restore',
        iconClass: 'material-symbols-outlined',
        route: '/process-list/restoration',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Delete Asset',
        icon: 'delete',
        iconClass: 'material-symbols-outlined',
        route: '/process-list/delete-asset',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Delete Delivery',
        icon: 'delete',
        iconClass: 'material-symbols-outlined',
        route: '/process-list/delete-delivery',
        permission: 'GALAXY_READER',
      },
    ],
  },
  {
    key: 'fusio',
    title: 'Fusio',
    icon: 'flare',
    zone: 'admin',
    items: [
      {
        name: 'Campaigns',
        icon: 'campaign',
        route: '/campaigns',
        permission: 'FUSIO_READER',
      },
      {
        name: 'Multi campaigns',
        icon: 'merge_type',
        iconClass: 'material-symbols-outlined',
        route: '/multi-campaigns-management',
        permission: 'FUSIO_READER',
      },
      {
        name: 'Search Engine Configs',
        icon: 'settings',
        route: '/search-engine-configs',
        permission: 'TELESCOPE_ACCESS',
      },
      {
        name: 'Rules',
        icon: 'sync',
        route: '/rules',
        permission: 'FUSIO_RULE',
      },
    ],
  },
  {
    key: 'admin',
    title: 'Admin',
    icon: 'settings',
    zone: 'admin',
    items: [
      {
        name: 'Artists',
        icon: 'album',
        route: '/artists',
        permission: 'GALAXY_READER',
      },
      {
        name: 'History',
        icon: 'manage_history',
        route: '/history',
        permission: 'GALAXY_READER',
      },
      {
        name: 'Import Flows',
        icon: 'save_alt',
        route: '/import-flows',
        permission: 'GALAXY_IMPORT_FLOW',
      },
      {
        name: 'Providers',
        icon: 'hub',
        route: '/providers',
        permission: 'GALAXY_ADMIN_PROVIDER',
      },
      {
        name: 'Themes',
        icon: 'category',
        route: '/themes',
        permission: 'GALAXY_ADMIN_THEME',
      },
    ],
  },
];
